import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import RoutingMachine from 'common/components/maps/RoutineMachine';
import RecenterMap from 'screens/vehicles-gps/components/recenterMap/RecenterMap';
import MarkerPopup from 'screens/vehicles-gps/components/markerPopup/MarkerPopup';
import TrackingService from 'api/tracking/TrackingService';
import { TrackingItemDto, TrackingPointItemDto } from 'api/tracking/models/TrackingItemDto';
import HotSpotsService from 'api/hotSpots/HotSpotsService';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import colors from 'styles/export/colors.module.scss';
import styles from './GpsMapWidget.module.scss';

// Declaração de tipo para ResizeObserver (pode não estar no lib dom de algumas versões do TS)
declare class ResizeObserver {
	constructor(callback: ResizeObserverCallback);
	observe(target: Element): void;
	unobserve(target: Element): void;
	disconnect(): void;
}

type ResizeObserverCallback = (entries: ResizeObserverEntry[]) => void;

interface ResizeObserverEntry {
	target: Element;
	contentRect: DOMRectReadOnly;
}

type HotSpotArea = { latitude: number; longitude: number; radius: number };

const FitBounds = ({ points }: { points: TrackingPointItemDto[] }) => {
	const map = useMap();

	useEffect(() => {
		if (points.length > 0) {
			const bounds = L.latLngBounds(points.map(p => p.position));
			map.fitBounds(bounds, { padding: [30, 30], maxZoom: 14 });
		}
	}, [points]);

	return null;
};

const InvalidateOnResize = () => {
	const map = useMap();

	useEffect(() => {
		const container = map.getContainer();
		let timeoutId: ReturnType<typeof setTimeout> | null = null;

		const observer = new ResizeObserver(() => {
			if (timeoutId) clearTimeout(timeoutId);
			timeoutId = setTimeout(() => {
				map.invalidateSize();
			}, 100);
		});

		observer.observe(container);

		return () => {
			if (timeoutId) clearTimeout(timeoutId);
			observer.disconnect();
		};
	}, [map]);

	return null;
};

type Props = {
	vehicleId?: string;
};

const GpsMapWidget = ({ vehicleId }: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [points, setPoints] = useState<TrackingPointItemDto[]>([]);
	const [pointsInfo, setPointsInfo] = useState<TrackingItemDto[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [refresh, setRefresh] = useState<number>(1);
	const [hotSpots, setHotSpots] = useState<SelectValueLabel[]>([]);
	const [hotSpotId, setHotSpotId] = useState<string>('');
	const [area, setArea] = useState<HotSpotArea | null>(null);

	useEffect(() => {
		// No detalhe do ativo não há seletor de hotspots — não é preciso carregá-los.
		if (!vehicleId) loadHotSpots();
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		loadPositions();
	}, [area]);

	const loadHotSpots = async () => {
		try {
			const result = await HotSpotsService.getMyHotSpots();
			setHotSpots(result || []);
			const selected = (result || []).find(x => (x as any).selected);
			if (selected) {
				setHotSpotId(selected.value);
				await applyHotSpot(selected.value);
			}
		} catch {
			addToast(t('common.messages.error_load_info'), { appearance: 'error' });
		}
	};

	const applyHotSpot = async (hotSpotId: string | null) => {
		if (!hotSpotId) {
			setArea(null);
			return;
		}
		try {
			const detail = await HotSpotsService.getById(hotSpotId);
			setArea({ latitude: detail.latitude, longitude: detail.longitude, radius: detail.radius });
		} catch {
			addToast(t('common.messages.error_load_info'), { appearance: 'error' });
		}
	};

	const loadPositions = async () => {
		try {
			setIsLoading(true);
			let items: TrackingItemDto[];
			if (vehicleId) {
				// Detalhe do ativo: buscar só a posição deste veículo (evita carregar a frota toda).
				items = (await TrackingService.getGpsVehiclesPosition([vehicleId])) ?? [];
			} else {
				const result = await TrackingService.getGpsVehiclesNearest({
					page: 1,
					itemsPerPage: 1000,
					latitude: area?.latitude ?? 0,
					longitude: area?.longitude ?? 0,
					radius: area?.radius ?? 0,
				});
				items = result?.items ?? [];
			}

			const validItems = items.filter(x => x.latitude && x.longitude);

			const mapped: TrackingPointItemDto[] = validItems.map(x => ({
				id: x.id,
				vehicleId: x.vehicleId,
				date: x.date,
				longitude: x.longitude,
				latitude: x.latitude,
				ignition: x.ignition,
				position: L.latLng(x.latitude ?? 0, x.longitude ?? 0)
			}));

			setPoints(mapped);
			setPointsInfo(validItems);
			setRefresh(r => r + 1);
		} catch {
			setPoints([]);
			setPointsInfo([]);
			addToast(t('common.messages.error_load_info'), { appearance: 'error' });
		} finally {
			setIsLoading(false);
		}
	};

	const loadIconColor = (i: number) => {
		return pointsInfo[i]?.ignition ? colors.pinIgnitionOn : colors.pinIgnitionOff;
	};

	const getSingleVehicleCenter = () => {
		if (points.length > 0) {
			return { lat: points[0].latitude ?? 0, lng: points[0].longitude ?? 0 };
		}
		return { lat: 39.5, lng: -8.0 };
	};

	const handleHotSpotChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const value = e.target.value;
		setHotSpotId(value);
		applyHotSpot(value || null);
	};

	const renderOptions = (
		<select
			className={styles.hotSpotSelect}
			value={hotSpotId}
			onChange={handleHotSpotChange}
		>
			<option value="">{t('vehicles_gps.select_hot_spot') as string}</option>
			{hotSpots.map(h => (
				<option key={h.value} value={h.value}>{h.label}</option>
			))}
		</select>
	);

	return (
		<Box
			title={vehicleId ? t('dashboard.gps_map_vehicle') as string : t('dashboard.gps_map') as string}
			renderOptions={vehicleId ? undefined : renderOptions}
			className={styles.container}
		 subTitleTooltip={t((vehicleId ? 'dashboard.gps_map_asset_tooltip' : 'dashboard.gps_map_tooltip') as any) as string}>
			{isLoading ? (
				<div className={styles.centerAlign}>
					<LoadingSpinner />
				</div>
			) : points.length === 0 ? (
				<div className={styles.emptyState}>
					{t('dashboard.gps_map_no_data')}
				</div>
			) : (
				<MapContainer
					key={`dashboard_map_${refresh}`}
					maxZoom={18}
					minZoom={5}
					zoom={6}
					scrollWheelZoom={true}
					zoomSnap={0.1}
					zoomDelta={0.5}
					className={styles.map}
					tap={false}
				>
					<InvalidateOnResize />
					<TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
					{vehicleId
						? <RecenterMap lat={getSingleVehicleCenter().lat} lng={getSingleVehicleCenter().lng} zoom={14} />
						: <FitBounds points={points} />
					}
					<RoutingMachine
						waypoints={points.map(x => x.position)}
						renderInfo={(i: number) => <MarkerPopup position={pointsInfo[i]} />}
						loadIconColor={loadIconColor}
					/>
				</MapContainer>
			)}
		</Box>
	);
};

export default GpsMapWidget;
